import {
  PalettesProps,
  presetDarkPalettes,
  presetPalettes,
} from '@ant-design/colors';
import { PaletteColorOptions, alpha } from '@mui/material/styles';

const getGreyPalette = (mode: 'light' | 'dark'): string[] =>
  mode === 'dark'
    ? [
        '#000000',
        '#141414',
        '#1e1e1e',
        '#595959',
        '#8c8c8c',
        '#bfbfbf',
        '#d9d9d9',
        '#f0f0f0',
        '#f5f5f5',
        '#fafafa',
        '#ffffff',
        // ascent
        '#fafafa',
        '#bfbfbf',
        '#434343',
        '#1f1f1f',
        '#121212',
        '#d3d8db',
      ]
    : [
        '#ffffff',
        '#fafafa',
        '#f5f5f5',
        '#f0f0f0',
        '#d9d9d9',
        '#bfbfbf',
        '#8c8c8c',
        '#595959',
        '#262626',
        '#141414',
        '#000000',
        // ascent
        '#fafafa',
        '#bfbfbf',
        '#434343',
        '#1f1f1f',
        '#fafafb',
        '#e6ebf1',
      ];

const createPalette = (mode: 'light' | 'dark') => {
  // these come from ant-design, some softer defaults
  const colors: PalettesProps =
    mode === 'dark' ? presetDarkPalettes : presetPalettes;

  const { blue, red, gold, cyan, green } = colors;

  // custom grey palette for dark mode support
  const greyPalette = getGreyPalette(mode);

  const grey: PaletteColorOptions = {
    50: greyPalette[1],
    100: greyPalette[2],
    200: greyPalette[3],
    300: greyPalette[4],
    400: greyPalette[5],
    500: greyPalette[6],
    600: greyPalette[7],
    700: greyPalette[8],
    800: greyPalette[9],
    900: greyPalette[10],
    A100: greyPalette[11],
    A200: greyPalette[12],
    A400: greyPalette[13],
    A700: greyPalette[14],
  };

  return {
    primary: {
      50: '#e6f4ff',
      100: blue[1],
      200: blue[2],
      400: blue[4],
      700: blue[7],
      900: blue[9],
      contrastText: '#fff',
      dark: blue[6],
      light: blue[3],
      main: blue[5],
    },
    secondary: {
      100: grey[100],
      200: grey[200],
      400: grey[400],
      600: grey[600],
      800: grey[800],
      A100: '#fff',
      A200: grey.A400,
      A300: grey.A700,
      contrastText: '#fff',
      dark: grey[700],
      light: grey[300],
      lighter: grey[100],
      main: grey[500]!,
    },
    error: {
      contrastText: '#fff',
      dark: red[7],
      light: red[2],
      main: red[4],
    },
    warning: {
      contrastText: grey[100],
      dark: gold[7],
      light: gold[3],
      main: gold[5],
    },
    info: {
      contrastText: '#fff',
      dark: cyan[7],
      light: cyan[3],
      main: cyan[5],
    },
    success: {
      contrastText: '#fff',
      dark: green[7],
      light: green[3],
      main: green[5],
    },
    grey: grey,
  };
};

const Palette = (mode: 'light' | 'dark' = 'light') => {
  const palette = createPalette(mode);

  return {
    mode,
    common: {
      black: '#000',
      white: '#fff',
    },
    ...palette,
    text: {
      primary:
        mode === 'dark' ? alpha(palette.grey[900]!, 0.87) : palette.grey[700],
      secondary:
        mode === 'dark' ? alpha(palette.grey[900]!, 0.45) : palette.grey[500],
      disabled:
        mode === 'dark' ? alpha(palette.grey[900]!, 0.1) : palette.grey[400],
    },
    action: {
      disabled: palette.grey[300],
    },
    divider:
      mode === 'dark' ? alpha(palette.grey[900]!, 0.05) : palette.grey[200],
    background: {
      paper: mode === 'dark' ? palette.grey[100] : '#fff',
      default: palette.grey[50],
    },
  };
};

export default Palette;
